import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api } from "../lib/api";
import { inr, fmtDate } from "../lib/format";
import { downloadCSV } from "../lib/csv";
import { ArrowDownRight, ArrowUpRight, BookText, Download } from "lucide-react";

const KIND_LABEL = {
  opening: "Opening Balance",
  deposit: "Deposit",
  deposit_reversal: "Deposit Reversed",
  withdrawal_hold: "Withdrawal",
  withdrawal_release: "Withdrawal Released",
  adjustment: "Adjustment",
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "credit", label: "Credit" },
  { key: "debit", label: "Debit" },
];

export const Passbook = () => {
  const [entries, setEntries] = useState([]);
  const [filter, setFilter] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  useEffect(() => {
    api.get("/passbook").then((r) => setEntries([...r.data].reverse()));
  }, []);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (filter === "credit" && e.delta < 0) return false;
      if (filter === "debit" && e.delta >= 0) return false;
      const d = (e.created_at || "").slice(0, 10);
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }, [entries, filter, from, to]);

  const exportCsv = () => {
    downloadCSV(
      "swiftors-passbook.csv",
      [
        { label: "Date & Time", value: (r) => fmtDate(r.created_at) },
        { label: "Description", value: (r) => r.reason || KIND_LABEL[r.kind] || r.kind },
        { label: "Type", value: (r) => (r.delta >= 0 ? "Credit" : "Debit") },
        { label: "Amount", value: (r) => r.delta },
        { label: "Running Balance", value: "balance_after" },
      ],
      filtered
    );
  };

  return (
    <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="passbook">
      <div className="flex flex-wrap items-center gap-3 px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <BookText size={16} className="text-[#059669]" />
          <h2 className="font-heading font-bold text-lg tracking-tight">Wallet Passbook</h2>
        </div>
        <div className="flex items-center gap-1 ml-auto">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              data-testid={`passbook-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-sm transition-colors ${
                filter === f.key ? "bg-foreground text-background" : "text-muted-foreground hover:bg-black/5"
              }`}
            >
              {f.label}
            </button>
          ))}
          <button
            data-testid="passbook-export"
            onClick={exportCsv}
            className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-sm border border-border hover:bg-black/5 transition-colors"
          >
            <Download size={13} /> CSV
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 px-5 py-3 border-b border-border/60 bg-secondary/40">
        <span className="overline">From</span>
        <input data-testid="passbook-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 rounded-sm border border-border bg-card px-2 text-xs" />
        <span className="overline">To</span>
        <input data-testid="passbook-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 rounded-sm border border-border bg-card px-2 text-xs" />
        {(from || to) && (
          <button onClick={() => { setFrom(""); setTo(""); }} className="text-xs text-muted-foreground underline">Clear</button>
        )}
      </div>

      {filtered.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground">No balance movements for this filter.</p>
      ) : (
        <div className="divide-y divide-border/40">
          {filtered.map((e, i) => {
            const credit = e.delta >= 0;
            return (
              <motion.div
                key={e.id}
                initial={{ opacity: 0, x: -6 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: Math.min(i * 0.03, 0.3) }}
                data-testid={`passbook-entry-${i}`}
                className="flex items-center gap-4 px-5 py-3.5 hover:bg-black/[0.03] transition-colors"
              >
                <div
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm"
                  style={{ backgroundColor: credit ? "#0596691a" : "#DC26261a", color: credit ? "#059669" : "#DC2626" }}
                >
                  {credit ? <ArrowDownRight size={16} /> : <ArrowUpRight size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{e.reason || KIND_LABEL[e.kind] || e.kind}</p>
                  <p className="text-xs text-muted-foreground">{fmtDate(e.created_at)}</p>
                </div>
                <div className="text-right">
                  <p className="font-mono-num text-sm font-semibold" style={{ color: credit ? "#059669" : "#DC2626" }}>
                    {credit ? "+" : "−"}{inr(Math.abs(e.delta))}
                  </p>
                  <p className="font-mono-num text-[11px] text-muted-foreground">Bal {inr(e.balance_after)}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};
